import {
  CSRF_HEADER,
  type DocumentOrigin,
  type FolderId,
  isImageMediaType,
  type ProjectDocument,
  type Reader,
  supportedMediaType,
  type UploadTarget,
  uploadLimitBytes,
  uploadLimitMbFor,
} from "@esi/shared";
import { api } from "../api.ts";
import { apiError } from "../api-errors.ts";
import { t } from "../i18n/i18n.ts";
import { putStorageFile } from "./storage-put.ts";

/**
 * The upload flow (specs/project-files "Direct upload with short-lived URLs"): declare the file
 * (intent), PUT it straight to storage with the one-time URL, then confirm. Nothing here keeps
 * the URL beyond the attempt that minted it.
 */

/** Browsers often leave `File.type` empty for Markdown and CSV: fall back on the extension. */
export const MEDIA_TYPES_BY_EXTENSION: Record<string, string> = {
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  csv: "text/csv",
  md: "text/markdown",
  markdown: "text/markdown",
  html: "text/html",
  htm: "text/html",
  txt: "text/plain",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
};

/** For the file picker's `accept`: extensions and media types ingestion supports. */
export const ACCEPTED_FILES = [
  ...Object.keys(MEDIA_TYPES_BY_EXTENSION).map((ext) => `.${ext}`),
  ...new Set(Object.values(MEDIA_TYPES_BY_EXTENSION)),
].join(",");

/** The supported media type of `file`, or null when ingestion cannot read it. */
export function fileMediaType(file: Pick<File, "name" | "type">): string | null {
  const declared = file.type ? supportedMediaType(file.type) : null;
  if (declared) {
    return declared;
  }
  const extension = file.name.includes(".") ? file.name.split(".").pop()?.toLowerCase() : "";
  return MEDIA_TYPES_BY_EXTENSION[extension ?? ""] ?? null;
}

/** Why `file` cannot be uploaded to a project limited to `maxUploadMb`, or null if it can. */
export function fileProblem(file: File, maxUploadMb: number): string | null {
  if (!fileMediaType(file)) {
    return t("upload.unsupportedType");
  }
  if (file.size === 0) {
    return t("upload.emptyFile");
  }
  const mediaType = fileMediaType(file) ?? "";
  const limitMb = uploadLimitMbFor(mediaType, maxUploadMb);
  if (file.size > uploadLimitBytes(limitMb)) {
    return isImageMediaType(mediaType) && limitMb < maxUploadMb
      ? t("upload.imageTooLarge", { mb: limitMb })
      : t("upload.tooLarge", { mb: maxUploadMb });
  }
  return null;
}

export interface UploadInput {
  projectId: string;
  file: File;
  readers: Reader[];
  classification: string;
  origin: DocumentOrigin;
  /** A Files folder of the project; the root when null or absent (knowledge-folders). */
  folderId?: FolderId | null;
}

export type UploadStep = "intent" | "put" | "confirm";

/**
 * A failed step; `resume` continues where it stopped (the PUT, or the confirmation). A `final`
 * failure (the tenant's storage is full, the project became read-only) has no retry; its `leftover` document, when one was
 * declared, will never be finished and is the caller's to remove.
 */
export class UploadError extends Error {
  constructor(
    readonly step: UploadStep,
    message: string,
    readonly resume?: Pending,
    readonly final?: { reason: "storage_limit" | "project_frozen"; leftover?: Pending },
  ) {
    super(message);
    this.name = "UploadError";
  }
}

/**
 * The caller aborted the upload. `pending` is the document declared before the abort landed,
 * when there is one: the caller removes it, unless its confirmation got through first.
 */
export class UploadCancelled extends Error {
  constructor(
    readonly step: UploadStep,
    readonly pending?: Pending,
  ) {
    super("Upload cancelled");
    this.name = "UploadCancelled";
  }
}

/** Progress and cancellation of one upload. */
export interface UploadHooks {
  onStep?: (step: UploadStep) => void;
  /** Bytes of the file sent to storage so far, out of `total`. */
  onProgress?: (sent: number, total: number) => void;
  signal?: AbortSignal;
}

/** An upload whose document exists but is not confirmed yet. */
export interface Pending {
  document: ProjectDocument;
  upload: UploadTarget;
  /** The object is in storage: only the confirmation is left. */
  stored?: boolean;
}

/** Whether `pending` can still be finished without declaring the file again. */
export const resumable = (pending: Pending) =>
  pending.stored === true || Date.parse(pending.upload.expiresAt) > Date.now();

/**
 * Runs intent → PUT → confirm. `onStep` reports progress. A failure after the intent carries
 * the pending upload, so `resumeUpload` can retry the PUT without a second document.
 */
export async function uploadDocument(
  input: UploadInput,
  csrfToken: string,
  hooks: UploadHooks = {},
): Promise<ProjectDocument> {
  const { signal } = hooks;
  // Each step is reported before the check, so a cancel asked from `onStep` lands at once.
  hooks.onStep?.("intent");
  checkCancelled("intent", signal);
  const mediaType = fileMediaType(input.file);
  if (!mediaType) {
    throw new UploadError("intent", t("upload.unsupportedType"));
  }
  const res = await network("intent", signal, () =>
    api.api.projects[":projectId"].documents.$post(
      {
        param: { projectId: input.projectId },
        json: {
          title: input.file.name,
          mediaType,
          sizeBytes: input.file.size,
          readers: input.readers,
          classification: input.classification,
          origin: input.origin,
          ...(input.folderId ? { folderId: input.folderId } : {}),
        },
      },
      { headers: { [CSRF_HEADER]: csrfToken }, init: { signal } },
    ),
  );
  if (!res.ok) {
    const error = await apiError(res);
    if (error.code === "storage_limit") {
      throw new UploadError("intent", t("upload.storageFull"), undefined, {
        reason: "storage_limit",
      });
    }
    throw new UploadError(
      "intent",
      error.status === 501 ? t("upload.storageUnavailable") : error.message,
    );
  }
  const pending = (await res.json()) as Pending;
  // Declared just as the abort landed: the document exists, so the caller can remove it.
  checkCancelled("intent", signal, pending);
  return resumeUpload(pending, input.file, input.projectId, csrfToken, hooks);
}

/** PUT (unless already stored) then confirm an upload whose intent already succeeded. */
export async function resumeUpload(
  pending: Pending,
  file: File,
  projectId: string,
  csrfToken: string,
  hooks: UploadHooks = {},
): Promise<ProjectDocument> {
  if (!resumable(pending)) {
    throw new UploadError("put", t("upload.expired"));
  }
  const stored = pending.stored ? pending : await put(pending, file, hooks);
  hooks.onStep?.("confirm");
  checkCancelled("confirm", hooks.signal, stored);
  return confirmStored(stored, projectId, csrfToken, hooks.signal);
}

/** PUT uses only the issued storage headers; pending targets stay attempt-local. */
async function put(pending: Pending, file: File, hooks: UploadHooks): Promise<Pending> {
  hooks.onStep?.("put");
  checkCancelled("put", hooks.signal, pending);
  try {
    await putStorageFile(pending.upload, file, hooks.signal, hooks.onProgress);
  } catch (error) {
    checkCancelled("put", hooks.signal, pending);
    throw new UploadError(
      "put",
      t(
        error instanceof Error && error.message === "storage_network"
          ? "errors.network"
          : "upload.putFailed",
      ),
      pending,
    );
  }
  return { ...pending, stored: true };
}

async function confirmStored(
  stored: Pending,
  projectId: string,
  csrfToken: string,
  signal?: AbortSignal,
): Promise<ProjectDocument> {
  const confirm = await network(
    "confirm",
    signal,
    () =>
      api.api.projects[":projectId"].documents[":documentId"].confirm.$post(
        { param: { projectId, documentId: stored.document.id } },
        { headers: { [CSRF_HEADER]: csrfToken }, init: { signal } },
      ),
    // Unanswered: the object is still stored, so only the confirmation is retried.
    stored,
  );
  if (!confirm.ok) {
    const error = await apiError(confirm);
    // The tenant's storage is full: the server removed the object, and sending it again would
    // be refused the same way. The declared document is left to remove.
    if (error.code === "storage_limit") {
      throw new UploadError("confirm", t("upload.storageFull"), undefined, {
        reason: "storage_limit",
        leftover: stored,
      });
    }
    // `conflict`: an API from before the distinct codes (one release of canary skew), where a
    // 409 meant either case: check whether it was confirmed, else send the file again.
    const legacy = error.status === 409 && error.code === "conflict";
    if (error.code === "upload_already_confirmed" || legacy) {
      // An earlier confirmation got through and its answer was lost.
      const confirmed = await confirmedDocument(stored, projectId);
      if (confirmed) {
        return confirmed;
      }
    }
    // apiError resolves the previous release's generic frozen-project refusal too.
    if (error.code === "project_frozen") {
      // No retry can finish it while the project is read-only: final, and the declared
      // document is left to remove.
      throw new UploadError("confirm", t("upload.frozen"), undefined, {
        reason: "project_frozen",
        leftover: stored,
      });
    }
    // Nothing stored (`upload_missing`), or the object did not match and was removed (422):
    // both can be sent again.
    if (error.code === "upload_missing" || legacy || error.status === 422) {
      throw new UploadError("confirm", mismatch(error.message), {
        ...stored,
        stored: false,
      });
    }
    // Anything else (5xx, a failed enqueue): the object is stored, so only confirm again.
    throw new UploadError("confirm", error.message, stored);
  }
  return (await confirm.json()) as ProjectDocument;
}

/** The document once it left `uploading`, or null (still unconfirmed, or unreadable). */
export async function confirmedDocument(
  stored: Pending,
  projectId: string,
): Promise<ProjectDocument | null> {
  try {
    const res = await api.api.projects[":projectId"].documents[":documentId"].$get({
      param: { projectId, documentId: stored.document.id },
    });
    const document = res.ok ? ((await res.json()) as ProjectDocument) : null;
    return document && document.status !== "uploading" ? document : null;
  } catch {
    return null;
  }
}

/** DELETE one document (its uploader or a project admin); the response is the caller's. */
export function deleteDocumentRequest(projectId: string, documentId: string, csrfToken: string) {
  return api.api.projects[":projectId"].documents[":documentId"].$delete(
    { param: { projectId, documentId } },
    { headers: { [CSRF_HEADER]: csrfToken } },
  );
}

/**
 * Runs one request; an abort becomes `UploadCancelled` and any other failure an `UploadError`
 * of `step`.
 */
async function network<T>(
  step: UploadStep,
  signal: AbortSignal | undefined,
  request: () => Promise<T>,
  resume?: Pending,
) {
  try {
    return await request();
  } catch {
    checkCancelled(step, signal, resume);
    throw new UploadError(step, t("errors.network"), resume);
  }
}

/** Throws `UploadCancelled` once `signal` is aborted. */
function checkCancelled(step: UploadStep, signal: AbortSignal | undefined, pending?: Pending) {
  if (signal?.aborted) {
    throw new UploadCancelled(step, pending);
  }
}

/** Best effort: removes an upload that will not be finished, so it does not linger. */
export async function abandonUpload(
  pending: Pending,
  projectId: string,
  csrfToken: string,
): Promise<void> {
  try {
    await deleteDocumentRequest(projectId, pending.document.id, csrfToken);
  } catch {
    // The incomplete upload stays listed; its uploader can delete it later.
  }
}

/** The server's detail as a sentence, then the way forward. */
const mismatch = (detail: string) =>
  t("upload.mismatch", { detail: /[.!?]$/.test(detail) ? detail : `${detail}.` });
